// Поиск по базе на телефоне (M8).
//
// Гонки ответов. На телефоне сеть медленнее и заметно рванее, чем на рабочем
// ноутбуке: ответ на «ипо» спокойно доезжает после ответа на «ипотека» и
// затирает свежий результат старым — на глазах у оператора. Поэтому у каждого
// запроса свой номер (`seqRef`), и применяется только ответ с последним
// номером; остальные выбрасываются молча — вместе со своими ошибками и снятием
// индикатора. Номер растёт в теле эффекта, а не внутри таймера: как только
// нажата следующая клавиша, всё, что уже летит по сети, устарело.
//
// Дебаунс короткий: он экономит запросы на медленной сети, но не должен
// превращаться в задержку, которую видно.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, notesApi } from '../api'
import type { NoteType, SearchHit } from '../protocol'
import './discover.css'

const DEBOUNCE_MS = 180
const LIMIT = 40

const TYPE_LABEL: Record<NoteType, string> = {
  goal: 'цель',
  project: 'проект',
  reference: 'справка',
  note: 'заметка',
  log: 'лог',
  message: 'сообщение',
  task: 'задача',
}

const TYPES: NoteType[] = ['goal', 'project', 'reference', 'note', 'log']

interface Part {
  text: string
  hit: boolean
}

/** Разбор `snippet`: сервер обрамляет совпадение маркерами <<< и >>>.
 *
 *  Разбираем строку сами и рисуем <mark>, а не отдаём в dangerouslySetInnerHTML:
 *  в теле заметки лежит что угодно, включая разметку, и вставлять её в DOM как
 *  есть нельзя. Непарные маркеры не ломают вывод — остаток уходит текстом. */
function splitSnippet(snippet: string): Part[] {
  const parts: Part[] = []
  let rest = snippet
  for (;;) {
    const open = rest.indexOf('<<<')
    if (open < 0) break
    const close = rest.indexOf('>>>', open + 3)
    if (close < 0) break
    if (open > 0) parts.push({ text: rest.slice(0, open), hit: false })
    parts.push({ text: rest.slice(open + 3, close), hit: true })
    rest = rest.slice(close + 3)
  }
  if (rest) parts.push({ text: rest, hit: false })
  return parts
}

/** Имя файла для заметки, которую предлагаем создать вместо пустого результата.
 *
 *  Запрос — произвольная строка, а из неё получается путь на диске: слэши и
 *  служебные символы вырезаем, ведущие точки тоже (иначе «..» и скрытые файлы).
 *  Кириллицу не трогаем — имена заметок человеческие. */
function notePath(title: string): string {
  const name = title
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+/, '')
    .trim()
  return `${name || 'без названия'}.md`
}

function errorText(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message
  return 'Не удалось выполнить поиск'
}

interface Props {
  onOpen(path: string): void
}

export default function SearchScreen({ onOpen }: Props) {
  const [query, setQuery] = useState('')
  const [type, setType] = useState<NoteType | null>(null)
  const [hits, setHits] = useState<SearchHit[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  /** номер последнего запущенного запроса — им отсекаются опоздавшие ответы */
  const seqRef = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const text = query.trim()

  // Поле — единственное, ради чего сюда заходят: фокус ставим сразу (M8.1).
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!text) {
      // Пустой запрос тоже сдвигает номер: ответ на предыдущие буквы не должен
      // всплыть уже после того, как поле очистили.
      seqRef.current += 1
      setHits([])
      setLoading(false)
      setError(null)
      return
    }
    const seq = ++seqRef.current
    setLoading(true)
    const timer = window.setTimeout(() => {
      notesApi
        .search(text, { type: type ?? undefined, limit: LIMIT })
        .then((found) => {
          if (seq !== seqRef.current) return
          setHits(found)
          setError(null)
          setLoading(false)
        })
        .catch((cause: unknown) => {
          if (seq !== seqRef.current) return
          setError(errorText(cause))
          setLoading(false)
        })
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [text, type])

  const create = useCallback(async () => {
    if (!text || creating) return
    setCreating(true)
    try {
      const note = await notesApi.create({ path: notePath(text), title: text, type: 'note' })
      onOpen(note.path)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось создать заметку')
    } finally {
      setCreating(false)
    }
  }, [text, creating, onOpen])

  // Подсветку пересобираем только при смене выдачи, а не на каждый рендер.
  const parts = useMemo(() => hits.map((hit) => splitSnippet(hit.snippet)), [hits])

  const empty = !!text && !loading && !error && hits.length === 0

  return (
    <div className="ab-msearch">
      <input
        ref={inputRef}
        className="ab-msearch__field"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="искать по базе"
        aria-label="поиск по базе"
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />

      {/* Чипы уезжают горизонтальным скроллом: в 390px шесть штук в строку
          не влезают, а перенос на вторую строку съедает экран выдачи (M8.1). */}
      <div className="ab-msearch__chips" role="group" aria-label="тип заметки">
        <button
          className={`ab-mchip${type === null ? ' is-on' : ''}`}
          onClick={() => setType(null)}
        >
          все типы
        </button>
        {TYPES.map((value) => (
          <button
            key={value}
            className={`ab-mchip${type === value ? ' is-on' : ''}`}
            onClick={() => setType(type === value ? null : value)}
          >
            {TYPE_LABEL[value]}
          </button>
        ))}
      </div>

      <div className="ab-msearch__status">
        {!text ? (
          <span>введите слово — ищем по заголовку и телу заметок</span>
        ) : error ? (
          <span className="ab-msearch__err">{error}</span>
        ) : loading ? (
          <span>ищу…</span>
        ) : (
          <span>{hits.length ? `найдено ${hits.length}` : 'ничего не нашлось'}</span>
        )}
      </div>

      {hits.map((hit, index) => (
        <button
          key={hit.path}
          type="button"
          className="ab-mhit"
          onClick={() => onOpen(hit.path)}
        >
          <span className="ab-mhit__head">
            <span className="ab-mhit__title">{hit.title}</span>
            <span className={`ab-mhit__type is-${hit.type}`}>{TYPE_LABEL[hit.type]}</span>
          </span>
          <span className="ab-mhit__snippet">
            {parts[index].map((part, at) =>
              part.hit ? (
                <mark key={at} className="ab-mhit__mark">{part.text}</mark>
              ) : (
                <span key={at}>{part.text}</span>
              ),
            )}
          </span>
          <span className="ab-mhit__path">{hit.path}</span>
        </button>
      ))}

      {empty ? (
        <div className="ab-msearch__empty">
          <p className="ab-msearch__emptyText">
            Ничего не нашлось. Можно завести заметку с таким названием.
          </p>
          <button
            className="ab-msearch__create"
            disabled={creating}
            onClick={() => void create()}
          >
            {creating ? 'создаю…' : `Создать «${text}»`}
          </button>
        </div>
      ) : null}
    </div>
  )
}
